/**
 * mega-menu.js
 * Dawn-compatible mega menu interactions for desktop and mobile.
 */
(() => {
  'use strict';

  const CFG = {
    desktopBreakpoint: 768,
    hoverOpenDelay: 60,
    hoverCloseDelay: 160,
    railDragThreshold: 8,
    scrollThreshold: 8,
    autoTabCooldown: 260,
  };

  const bodyLocks = new Set();
  let cartUpdater = null;
  let globalsBound = false;
  let layoutFrame = null;

  const qs = (selector, context = document) => (context ? context.querySelector(selector) : null);
  const qsa = (selector, context = document) => (context ? [...context.querySelectorAll(selector)] : []);

  const isDesktopViewport = () => window.innerWidth > CFG.desktopBreakpoint;
  const supportsHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isCompactSearchViewport = () => window.matchMedia('(max-width: 768px)').matches;

  function syncBodyLock() {
    const locked = bodyLocks.size > 0;
    document.documentElement.classList.toggle('mm-lock-scroll', locked);
    document.body.classList.toggle('mm-lock-scroll', locked);
  }

  function setBodyLock(key, shouldLock) {
    if (!key) return;
    if (shouldLock) {
      bodyLocks.add(key);
    } else {
      bodyLocks.delete(key);
    }
    syncBodyLock();
  }

  function closeWishlistSurface() {
    if (window.Wishlist && typeof window.Wishlist.closeDrawer === 'function') {
      window.Wishlist.closeDrawer();
    }
  }

  function closeCustomerPanels(context = document) {
    qsa('[data-mm-customer-panel].is-open', context).forEach((panel) => {
      panel.mmClose?.();
    });
  }

  function getDesktopItems() {
    return qsa('.mm-nav__item--has-mega').map((item) => ({
      item,
      trigger: qs('[data-mm-mega-trigger]', item),
    })).filter(({ trigger }) => Boolean(trigger));
  }

  function focusFirstFocusable(container) {
    const firstFocusable = qs('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])', container);
    firstFocusable?.focus();
  }

  function closeMega(item, trigger, { focusTrigger = false } = {}) {
    item?.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');

    if (focusTrigger) {
      trigger?.focus();
    }
  }

  function closeAllMegas(exceptItem = null) {
    getDesktopItems().forEach(({ item, trigger }) => {
      if (item !== exceptItem) {
        closeMega(item, trigger);
      }
    });
  }

  function openMega(item, trigger) {
    closeAllMegas(item);
    item?.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    queueLayoutSync();
  }

  function updateStickyState() {
    const scrolled = window.scrollY > CFG.scrollThreshold;

    qsa('[data-mm-header]').forEach((header) => {
      header.classList.toggle('is-scrolled', scrolled);
    });

    qsa('[data-mm-mobile-header]').forEach((header) => {
      header.classList.toggle('is-scrolled', scrolled);
    });
  }

  function syncLayoutVars() {
    qsa('[data-mm-shell]').forEach((shell) => {
      const desktopHeader = qs('[data-mm-header]', shell);
      const mobileHeader = qs('[data-mm-mobile-header]', shell);

      if (desktopHeader && getComputedStyle(desktopHeader).display !== 'none') {
        const desktopRect = desktopHeader.getBoundingClientRect();
        const megaTop = Math.max(desktopRect.bottom, desktopHeader.offsetHeight, 0);
        shell.style.setProperty('--mm-mega-top', `${megaTop}px`);
      }

      if (mobileHeader && getComputedStyle(mobileHeader).display !== 'none') {
        const mobileRect = mobileHeader.getBoundingClientRect();
        const mobileTop = Math.max(mobileRect.bottom, mobileHeader.offsetHeight, 54);
        shell.style.setProperty('--mm-mobile-overlay-top', `${mobileTop}px`);
      }
    });
  }

  function queueLayoutSync() {
    if (layoutFrame) return;

    layoutFrame = window.requestAnimationFrame(() => {
      layoutFrame = null;
      updateStickyState();
      syncLayoutVars();
    });
  }

  function sanitizeText(value = '') {
    return String(value).replace(/\s+/g, ' ').trim();
  }

  function escapeHtml(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getPredictiveSectionId(shell) {
    return shell?.dataset.mmPredictiveSection || 'mm-predictive-search';
  }

  async function fetchPredictiveMarkup(query, predictiveSection, { signal, limit = 4 } = {}) {
    const params = new URLSearchParams();
    params.set('q', query.trim());
    params.set('section_id', predictiveSection);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(limit));
    params.set('resources[options][unavailable_products]', 'hide');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${rootUrl}search/suggest?${params.toString()}`, { signal });
    const text = await response.text();

    if (!response.ok) {
      throw new Error(text);
    }

    const parsed = new DOMParser().parseFromString(text, 'text/html');
    const renderedSection = parsed.querySelector(`#shopify-section-${predictiveSection}`);

    if (!renderedSection) {
      throw new Error('Predictive search section missing');
    }

    return renderedSection.innerHTML;
  }

  function setupDesktopMega(root = document) {
    qsa('[data-mm-nav-item]', root).forEach((item) => {
      if (item.dataset.mmDesktopBound === 'true') return;

      const trigger = qs('[data-mm-mega-trigger]', item);
      const mega = qs('[data-mm-mega]', item);

      if (!trigger || !mega) return;

      item.dataset.mmDesktopBound = 'true';

      let openTimer = null;
      let closeTimer = null;

      const cancelTimers = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
      };

      const scheduleOpen = () => {
        cancelTimers();
        if (!isDesktopViewport()) return;

        openTimer = window.setTimeout(() => {
          openMega(item, trigger);
        }, CFG.hoverOpenDelay);
      };

      const scheduleClose = () => {
        cancelTimers();

        closeTimer = window.setTimeout(() => {
          closeMega(item, trigger);
        }, CFG.hoverCloseDelay);
      };

      item.addEventListener('mouseenter', () => {
        if (supportsHover()) {
          scheduleOpen();
        }
      });

      item.addEventListener('mouseleave', () => {
        if (supportsHover()) {
          scheduleClose();
        }
      });

      item.addEventListener('focusin', () => {
        if (isDesktopViewport()) {
          openMega(item, trigger);
        }
      });

      item.addEventListener('focusout', (event) => {
        const nextFocused = event.relatedTarget;
        if (!nextFocused || !item.contains(nextFocused)) {
          scheduleClose();
        }
      });

      trigger.addEventListener('click', (event) => {
        if (!isDesktopViewport() || supportsHover()) return;

        if (!item.classList.contains('is-open')) {
          event.preventDefault();
          openMega(item, trigger);
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (item.classList.contains('is-open')) {
            closeMega(item, trigger);
          } else {
            openMega(item, trigger);
          }
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          openMega(item, trigger);
          focusFirstFocusable(mega);
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeMega(item, trigger, { focusTrigger: true });
        }
      });

      mega.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMega(item, trigger, { focusTrigger: true });
        }
      });
    });
  }

  function setupSearch(root = document) {
    qsa('[data-mm-search-overlay]', root).forEach((overlay) => {
      if (overlay.dataset.mmSearchBound === 'true') return;

      overlay.dataset.mmSearchBound = 'true';

      const overlayId = overlay.id || 'mm-search-overlay';
      const input = qs('.mm-search-input', overlay);
      const form = qs('[data-mm-search-form]', overlay);
      const backdrop = qs('.mm-search-overlay__backdrop', overlay);
      const closeButtons = qsa('[data-mm-search-close]', overlay);
      const shell = overlay.closest('[data-mm-shell]');
      const openButtons = qsa('[data-mm-search-open]', shell || document);
      const defaultView = qs('[data-mm-search-default]', overlay);
      const resultsView = qs('[data-mm-search-results]', overlay);
      const loadingView = qs('[data-mm-search-loading]', overlay);
      const searchChips = qsa('[data-mm-search-chip]', overlay);
      const predictiveSection = shell?.dataset.mmPredictiveSection || 'mm-predictive-search';
      const predictiveEnabled = shell?.dataset.mmSearchEnabled !== 'false';
      const lockKey = `search-${overlayId}`;
      let debounceId = null;
      let activeController = null;

      const setSearchState = ({ hasQuery = false, isLoading = false, hasResults = false } = {}) => {
        overlay.classList.toggle('has-query', hasQuery);
        overlay.classList.toggle('is-searching', isLoading);
        overlay.classList.toggle('has-results', hasResults);
      };

      const showDefaultView = () => {
        defaultView?.setAttribute('hidden', 'hidden');
        if (resultsView) {
          resultsView.hidden = true;
          resultsView.innerHTML = '';
        }
        if (loadingView) loadingView.hidden = true;
        setSearchState();
      };

      const showLoading = () => {
        defaultView?.setAttribute('hidden', 'hidden');
        if (resultsView) resultsView.hidden = true;
        if (loadingView) loadingView.hidden = false;
        setSearchState({ hasQuery: true, isLoading: true });
      };

      const showTypingState = () => {
        if (!(input?.value || '').trim()) {
          showDefaultView();
          return;
        }

        defaultView?.setAttribute('hidden', 'hidden');
        if (resultsView) resultsView.hidden = true;
        if (loadingView) loadingView.hidden = false;
        setSearchState({ hasQuery: true, isLoading: true });
      };

      const showResults = (markup) => {
        defaultView?.setAttribute('hidden', 'hidden');
        if (loadingView) loadingView.hidden = true;
        if (resultsView) {
          resultsView.hidden = false;
          resultsView.innerHTML = markup;
        }
        setSearchState({ hasQuery: true, hasResults: true });
      };

      const showSearchError = () => {
        showResults(`
          <div class="mm-predictive-search__empty">
            <p class="mm-predictive-search__eyebrow">Search unavailable</p>
            <h3 class="mm-predictive-search__title">Please try again in a moment</h3>
            <p class="mm-predictive-search__note">We couldn't load live suggestions right now.</p>
          </div>
        `);
      };

      const fetchPredictiveResults = async (term) => {
        const query = term.trim();

        if (!query) {
          activeController?.abort();
          showDefaultView();
          return;
        }

        activeController?.abort();
        activeController = new AbortController();
        showLoading();

        try {
          const markup = await fetchPredictiveMarkup(query, predictiveSection, {
            signal: activeController.signal,
            limit: 3,
          });
          showResults(markup);
        } catch (error) {
          if (error.name === 'AbortError') return;
          showSearchError();
        }
      };

      const queuePredictiveResults = () => {
        if (!predictiveEnabled) {
          showDefaultView();
          return;
        }

        window.clearTimeout(debounceId);
        showTypingState();
        debounceId = window.setTimeout(() => {
          fetchPredictiveResults(input?.value || '');
        }, 180);
      };

      const open = () => {
        closeCustomerPanels(shell || document);
        closeWishlistSurface();

        if (isCompactSearchViewport()) {
          const inlineSearch = qs('[data-mm-mobile-inline-search]', shell || document);
          if (inlineSearch?.mmOpen) {
            window.setTimeout(() => {
              inlineSearch.mmOpen();
            }, 40);
            return;
          }
        }

        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        setBodyLock(lockKey, false);
        if (!predictiveEnabled || !(input?.value || '').trim()) {
          showDefaultView();
        } else {
          queuePredictiveResults();
        }
        window.setTimeout(() => input?.focus(), 80);
      };

      const close = () => {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        setBodyLock(lockKey, false);
        window.clearTimeout(debounceId);
        activeController?.abort();
        if (input) input.value = '';
        showDefaultView();
      };

      overlay.mmClose = close;

      openButtons.forEach((button) => {
        if (button.dataset.mmSearchOpenBound === 'true') return;
        button.dataset.mmSearchOpenBound = 'true';
        button.addEventListener('click', open);
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', close);
      });

      backdrop?.addEventListener('click', close);

      input?.addEventListener('input', queuePredictiveResults);
      input?.addEventListener('focus', () => {
        if (predictiveEnabled && (input.value || '').trim()) {
          queuePredictiveResults();
        }
      });

      form?.addEventListener('submit', (event) => {
        if (!(input?.value || '').trim()) {
          event.preventDefault();
          input?.focus();
        }
      });

      searchChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          if (!input) return;
          input.value = chip.dataset.mmSearchChip || '';
          if (predictiveEnabled) {
            fetchPredictiveResults(input.value);
          }
          input.focus();
        });
      });

      showDefaultView();
    });
  }

  function setupMobileInlineSearch(root = document) {
    qsa('[data-mm-mobile-inline-search]', root).forEach((search) => {
      if (search.dataset.mmMobileInlineSearchBound === 'true') return;

      search.dataset.mmMobileInlineSearchBound = 'true';

      const shell = search.closest('[data-mm-shell]');
      const form = qs('[data-mm-mobile-search-form]', search);
      const input = qs('[data-mm-mobile-search-input]', search);
      const clearButton = qs('[data-mm-mobile-search-clear]', search);
      const results = qs('[data-mm-mobile-search-results]', search);
      const loading = qs('[data-mm-mobile-search-loading]', search);
      const predictiveSection = getPredictiveSectionId(shell);
      const predictiveEnabled = shell?.dataset.mmSearchEnabled !== 'false';
      let debounceId = null;
      let activeController = null;

      const setState = ({ active = false, loadingState = false, resultsState = false } = {}) => {
        search.classList.toggle('is-active', active);
        search.classList.toggle('is-loading', loadingState);
        search.classList.toggle('has-results', resultsState);
        if (clearButton) {
          clearButton.hidden = !(input?.value || '').trim();
        }
      };

      const close = ({ clearQuery = false } = {}) => {
        window.clearTimeout(debounceId);
        activeController?.abort();

        if (clearQuery && input) {
          input.value = '';
        }

        if (loading) loading.hidden = true;
        if (results) {
          results.hidden = true;
          results.innerHTML = '';
        }

        setState();
      };

      const open = () => {
        if (!isCompactSearchViewport()) return false;
        closeCustomerPanels(shell || document);
        closeWishlistSurface();
        search.classList.add('is-active');
        const rect = search.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          search.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        window.setTimeout(() => input?.focus(), 30);
        if ((input?.value || '').trim()) {
          queueResults();
        } else {
          setState({ active: true });
        }
        return true;
      };

      const showLoading = () => {
        if (results) {
          results.hidden = true;
          results.innerHTML = '';
        }
        if (loading) loading.hidden = false;
        setState({ active: true, loadingState: true });
      };

      const showResults = (markup) => {
        if (loading) loading.hidden = true;
        if (results) {
          results.hidden = false;
          results.innerHTML = markup;
        }
        setState({ active: true, resultsState: true });
      };

      const showError = () => {
        showResults(`
          <div class="mm-predictive-search__empty">
            <h3 class="mm-predictive-search__title">Search unavailable</h3>
            <p class="mm-predictive-search__note">Please try again in a moment.</p>
          </div>
        `);
      };

      const fetchResults = async (term) => {
        const query = term.trim();
        if (!query) {
          close();
          return;
        }

        activeController?.abort();
        activeController = new AbortController();
        showLoading();

        try {
          const markup = await fetchPredictiveMarkup(query, predictiveSection, {
            signal: activeController.signal,
            limit: 4,
          });
          showResults(markup);
        } catch (error) {
          if (error.name === 'AbortError') return;
          showError();
        }
      };

      function queueResults() {
        window.clearTimeout(debounceId);

        const query = (input?.value || '').trim();
        if (!query) {
          close();
          return;
        }

        if (!predictiveEnabled) {
          setState({ active: true });
          if (loading) loading.hidden = true;
          if (results) {
            results.hidden = true;
            results.innerHTML = '';
          }
          return;
        }

        setState({ active: true, loadingState: true });
        if (loading) loading.hidden = false;
        debounceId = window.setTimeout(() => {
          fetchResults(query);
        }, 160);
      }

      search.mmOpen = open;
      search.mmClose = close;

      input?.addEventListener('focus', () => {
        if (predictiveEnabled && (input.value || '').trim()) {
          queueResults();
        } else {
          setState({ active: true });
        }
      });

      input?.addEventListener('input', () => {
        queueResults();
      });

      input?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          close();
          input.blur();
        }
      });

      form?.addEventListener('submit', (event) => {
        if (!(input?.value || '').trim()) {
          event.preventDefault();
          input?.focus();
        } else {
          close();
        }
      });

      clearButton?.addEventListener('click', () => {
        close({ clearQuery: true });
        input?.focus();
      });

      results?.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) {
          close();
        }
      });

      document.addEventListener('click', (event) => {
        if (!search.contains(event.target) && !event.target.closest('[data-mm-search-open]')) {
          close();
        }
      });

      window.addEventListener('scroll', () => {
        if (search.classList.contains('is-active')) {
          close();
        }
      }, { passive: true });

      close();
    });
  }

  async function openThemeCartDrawer(trigger) {
    const cartDrawer = window.CartDrawer || document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open(trigger);
      return true;
    }

    if (typeof window.openCartDrawer === 'function') {
      window.openCartDrawer(trigger);
      return true;
    }

    return false;
  }

  window.MegaMenuCart = window.MegaMenuCart || {};
  window.MegaMenuCart.open = openThemeCartDrawer;
  window.MegaMenuCart.refreshBadges = () => {
    if (typeof cartUpdater === 'function') {
      cartUpdater();
    }
  };

  function getCartCountFromPayload(payload) {
    const detail = payload && payload.detail ? payload.detail : null;
    const rawCount =
      detail && detail.cart && Number.isFinite(Number(detail.cart.item_count))
        ? Number(detail.cart.item_count)
        : detail && Number.isFinite(Number(detail.itemCount))
          ? Number(detail.itemCount)
          : payload && Number.isFinite(Number(payload.item_count))
            ? Number(payload.item_count)
            : null;

    return rawCount === null ? null : Math.max(0, rawCount);
  }

  function isVisualCartButton(button) {
    return Boolean(
      button &&
        (
          button.classList.contains('mm-action-btn') ||
          button.classList.contains('mm-mobile-btn') ||
          button.classList.contains('mm-drawer-action') ||
          button.classList.contains('mm-mobile-bottom-nav__item')
        )
    );
  }

  function ensureCartBadge(button) {
    if (!button || !isVisualCartButton(button)) return null;

    if (button.classList.contains('mm-mobile-bottom-nav__item')) {
      const icon = qs('.mm-mobile-bottom-nav__icon', button);
      if (!icon) return null;

      let badge = qs('.mm-mobile-bottom-nav__badge', icon);
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'mm-mobile-bottom-nav__badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.style.display = 'none';
        icon.appendChild(badge);
      }
      return badge;
    }

    let badge = qs('.mm-cart-badge', button);
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'mm-cart-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.style.display = 'none';
      button.appendChild(badge);
    }
    return badge;
  }

  function animateCartBadge(badge) {
    if (!badge) return;
    badge.classList.remove('is-syncing');
    // Reflow lets the pop animation retrigger cleanly on each count change.
    void badge.offsetWidth;
    badge.classList.add('is-syncing');
  }

  function updateCartButtons(count) {
    qsa('[data-mm-cart-button]').forEach((button) => {
      if (!button.dataset.mmCartLabelBase) {
        const label = (button.getAttribute('aria-label') || 'Cart').replace(/\s*\((?:\d+|\d+\+|99\+)\)\s*$/, '').trim();
        button.dataset.mmCartLabelBase = label || 'Cart';
      }

      button.setAttribute('aria-label', `${button.dataset.mmCartLabelBase} (${count})`);

      const badge = ensureCartBadge(button);
      if (!badge) return;

      const nextValue = count > 99 ? '99+' : String(count);
      const previousValue = badge.dataset.mmCount || badge.textContent.trim();

      if (count <= 0) {
        badge.dataset.mmCount = '0';
        badge.textContent = '0';
        badge.style.display = 'none';
        badge.classList.remove('is-syncing');
        return;
      }

      badge.style.display = '';
      badge.textContent = nextValue;
      badge.dataset.mmCount = nextValue;

      if (previousValue !== nextValue) {
        animateCartBadge(badge);
      }
    });
  }

  function setupCartCount(root = document) {
    qsa('[data-mm-cart-button]', root).forEach((button) => {
      if (button.dataset.mmCartBound === 'true') return;
      button.dataset.mmCartBound = 'true';

      button.addEventListener('click', async (event) => {
        const opened = await openThemeCartDrawer(button);
        if (opened) {
          event.preventDefault();
        }
      });
    });

    if (!cartUpdater) {
      cartUpdater = async (payload) => {
        const eventCount = getCartCountFromPayload(payload);
        if (eventCount !== null) {
          updateCartButtons(eventCount);
          return;
        }

        try {
          const response = await fetch('/cart.js', { cache: 'no-store' });
          if (!response.ok) return;

          const cart = await response.json();
          updateCartButtons(cart.item_count || 0);
        } catch (_) {
          // Silent fail to avoid impacting the storefront if /cart.js is unavailable.
        }
      };

      document.addEventListener('cart:refresh', cartUpdater);
      document.addEventListener('cart-update', cartUpdater);
      document.addEventListener('cart:updated', cartUpdater);
    }

    cartUpdater();
  }

  function highlightActivePill(rail) {
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';

    qsa('[data-mm-pill]', rail).forEach((pill) => {
      const href = pill.getAttribute('href');
      if (!href || href === '#') return;

      try {
        const pillUrl = new URL(href, window.location.origin);
        const pillPath = pillUrl.pathname.replace(/\/+$/, '') || '/';

        if (pillPath !== '/' && currentPath.startsWith(pillPath)) {
          pill.classList.add('mm-pill--active');
          window.setTimeout(() => {
            pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }, 250);
        }
      } catch (_) {
        // Ignore invalid URLs; Shopify sometimes stores placeholder values while editing.
      }
    });
  }

  function syncOuterRail(shell, { handle = '', url = '' } = {}) {
    if (!shell || shell.dataset.mmMobileSyncRail === 'false') return;

    const rail = qs('[data-mm-rail]', shell);
    if (!rail) return;

    const normalizedUrl = normalizePath(url);
    let activePill = null;

    qsa('[data-mm-pill]', rail).forEach((pill) => {
      const pillHandle = pill.dataset.mmPillHandle || pill.dataset.mmDrawerTarget || '';
      const pillUrl = normalizePath(pill.dataset.mmPillUrl || pill.getAttribute('href') || '');
      const matchesHandle = handle && pillHandle === handle;
      const matchesUrl = normalizedUrl && normalizedUrl !== '/' && pillUrl === normalizedUrl;
      const isActive = matchesHandle || matchesUrl;

      pill.classList.toggle('mm-pill--active', isActive);
      if (isActive) activePill = pill;
    });

    activePill?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  function normalizePath(url = '') {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.replace(/\/+$/, '') || '/';
    } catch (_) {
      return '/';
    }
  }

  function activateDrawerPanel(drawer, target = '') {
    const tabs = qsa('[data-mm-panel-trigger]', drawer);
    const panels = qsa('[data-mm-panel]', drawer);

    if (!tabs.length || !panels.length) return false;

    let activeTab = null;

    if (target) {
      activeTab = tabs.find((tab) =>
        tab.dataset.mmPanelTrigger === target || tab.dataset.mmPanelHandle === target
      );
    }

    if (!activeTab) {
      const currentPath = normalizePath(window.location.pathname);
      activeTab = tabs.find((tab) => {
        const panel = panels.find((entry) => entry.dataset.mmPanel === tab.dataset.mmPanelTrigger);
        if (!panel) return false;

        return qsa('a[href]', panel).some((link) => {
          const panelPath = normalizePath(link.getAttribute('href') || '');
          return panelPath !== '/' && currentPath.startsWith(panelPath);
        });
      });
    }

    if (!activeTab) {
      activeTab = tabs[0];
    }

    const activePanelId = activeTab.dataset.mmPanelTrigger;
    const activePanel = panels.find((panel) => panel.dataset.mmPanel === activePanelId);
    const shell = drawer.closest('[data-mm-shell]');

    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.mmPanel === activePanelId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    if (activePanel) {
      activePanel.scrollTop = 0;
    }

    syncOuterRail(shell, {
      handle: activeTab.dataset.mmPanelHandle || '',
      url: activeTab.dataset.mmPanelUrl || '',
    });

    activeTab.scrollIntoView({ block: 'center', inline: 'nearest' });
    return true;
  }

  function setupMobileRailLayers(root = document) {
    qsa('[data-mm-rail-layer]', root).forEach((layer) => {
      if (layer.dataset.mmRailLayerBound === 'true') return;

      layer.dataset.mmRailLayerBound = 'true';

      const shell = layer.closest('[data-mm-shell]');
      const handle = layer.dataset.mmRailLayer || '';
      const mobileHeader = layer.closest('.mm-mobile-header');
      const getHideRailPreference = () => {
        if (layer.dataset.mmRailLayerHideRail === 'true') return true;
        if (layer.dataset.mmRailLayerHideRail === 'false') return false;
        return (shell?.dataset.mmMobileRailLayerHideRail || 'true') !== 'false';
      };
      const getTriggers = () =>
        qsa('[data-mm-rail-layer-trigger]', shell || document).filter(
          (trigger) => trigger.dataset.mmRailLayerTrigger === handle
        );
      const syncHeaderState = () => {
        if (!mobileHeader) return;

        const rail = qs('[data-mm-rail]', mobileHeader);
        const openLayer = qsa('[data-mm-rail-layer].is-open', mobileHeader)[0] || null;
        const hasOpenLayer = Boolean(openLayer);

        mobileHeader.classList.toggle('has-inline-layer', hasOpenLayer);
        mobileHeader.dataset.mmRailLayerActive = hasOpenLayer ? 'true' : 'false';
        mobileHeader.dataset.mmRailLayerHandle = hasOpenLayer ? (openLayer?.dataset.mmRailLayer || '') : '';

        if (rail) {
          const shouldHideRail = hasOpenLayer && getHideRailPreference();
          rail.classList.toggle('is-layer-hidden', shouldHideRail);
          rail.hidden = shouldHideRail;
          rail.setAttribute('aria-hidden', shouldHideRail ? 'true' : 'false');
        }

        getTriggers().forEach((trigger) => {
          const isExpanded = layer.classList.contains('is-open');
          trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        });
      };

      const openLayer = () => {
        closeCustomerPanels(shell || document);
        closeWishlistSurface();

        qsa('[data-mm-rail-layer].is-open', shell || document).forEach((entry) => {
          if (entry !== layer) {
            entry.mmClose?.();
          }
        });

        qsa('.mm-mobile-drawer.is-open', shell || document).forEach((drawer) => {
          drawer.mmClose?.();
        });

        layer.classList.add('is-open');
        layer.setAttribute('aria-hidden', 'false');
        getTriggers().forEach((trigger) => trigger.classList.add('is-layer-open'));
        syncHeaderState();

        const body = qs('.mm-rail-layer__body', layer);
        if (body) {
          body.scrollTop = 0;
        }

        window.requestAnimationFrame(() => {
          layer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      };

      const closeLayer = () => {
        layer.classList.remove('is-open');
        layer.setAttribute('aria-hidden', 'true');
        getTriggers().forEach((trigger) => trigger.classList.remove('is-layer-open'));
        syncHeaderState();
      };

      const toggleLayer = () => {
        if (layer.classList.contains('is-open')) {
          closeLayer();
        } else {
          openLayer();
        }
      };

      layer.mmOpen = openLayer;
      layer.mmClose = closeLayer;
      layer.mmToggle = toggleLayer;

      qsa('[data-mm-rail-layer-close]', layer).forEach((button) => {
        button.addEventListener('click', closeLayer);
      });

      qsa('a[href]', layer).forEach((link) => {
        link.addEventListener('click', () => {
          if ((link.getAttribute('href') || '') !== '#') {
            closeLayer();
          }
        });
      });

      syncHeaderState();
    });

    qsa('[data-mm-rail-layer-trigger]', root).forEach((trigger) => {
      if (trigger.dataset.mmRailLayerTriggerBound === 'true') return;

      trigger.dataset.mmRailLayerTriggerBound = 'true';

      trigger.addEventListener('click', (event) => {
        if (event.defaultPrevented || isDesktopViewport()) return;

        event.preventDefault();

        const shell = trigger.closest('[data-mm-shell]');
        const target = trigger.dataset.mmRailLayerTrigger || '';
        const layer = qsa('[data-mm-rail-layer]', shell || document).find(
          (entry) => entry.dataset.mmRailLayer === target
        );

        layer?.mmToggle?.();
      });
    });
  }

  function setupMobileRail(root = document) {
    qsa('[data-mm-rail]', root).forEach((rail) => {
      if (rail.dataset.mmRailBound === 'true') return;

      rail.dataset.mmRailBound = 'true';

      let isDragging = false;
      let dragMoved = false;
      let startX = 0;
      let startScrollLeft = 0;

      highlightActivePill(rail);

      rail.addEventListener('pointerdown', (event) => {
        isDragging = true;
        dragMoved = false;
        startX = event.clientX;
        startScrollLeft = rail.scrollLeft;
        rail.style.cursor = 'grabbing';
        rail.style.userSelect = 'none';
        rail.setPointerCapture(event.pointerId);
      });

      rail.addEventListener('pointermove', (event) => {
        if (!isDragging) return;

        const deltaX = event.clientX - startX;
        if (Math.abs(deltaX) > CFG.railDragThreshold) {
          dragMoved = true;
        }

        rail.scrollLeft = startScrollLeft - deltaX;
      });

      const finishDrag = () => {
        isDragging = false;
        rail.style.cursor = '';
        rail.style.userSelect = '';
      };

      rail.addEventListener('pointerup', finishDrag);
      rail.addEventListener('pointercancel', finishDrag);
      rail.addEventListener('lostpointercapture', finishDrag);

      rail.addEventListener('click', (event) => {
        const pill = event.target.closest('[data-mm-pill]');
        if (!pill) return;

        if (dragMoved) {
          event.preventDefault();
          dragMoved = false;
          return;
        }

        const href = pill.getAttribute('href') || '';
        const drawerTarget = pill.dataset.mmDrawerTarget;
        const layerTarget = pill.dataset.mmRailLayerTrigger || '';

        if (layerTarget) {
          const shell = pill.closest('[data-mm-shell]');
          const layer = qsa('[data-mm-rail-layer]', shell).find((entry) => entry.dataset.mmRailLayer === layerTarget);
          if (layer?.mmToggle) {
            event.preventDefault();
            layer.mmToggle();
            return;
          }
        }

        if (drawerTarget && (!href || href === '#')) {
          const shell = pill.closest('[data-mm-shell]');
          const drawer = qs('.mm-mobile-drawer', shell);
          if (drawer?.mmOpen) {
            event.preventDefault();
            drawer.mmOpen(drawerTarget);
          }
        }
      }, true);

      qsa('[data-mm-pill]', rail).forEach((pill) => {
        pill.addEventListener('focus', () => {
          pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
      });
    });
  }

  function scrollDrawerToGroup(drawer, target) {
    if (!target) return;

    const group = qsa('[data-mm-drawer-group]', drawer).find((entry) => entry.dataset.mmDrawerGroup === target);
    group?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function setupMobileDrawer(root = document) {
    qsa('.mm-mobile-drawer', root).forEach((drawer) => {
      if (drawer.dataset.mmDrawerBound === 'true') return;

      drawer.dataset.mmDrawerBound = 'true';

      const shell = drawer.closest('[data-mm-shell]');
      const backdrop = qs('.mm-drawer-backdrop', drawer);
      const closeButton = qs('.mm-drawer-close', drawer);
      const toggleButtons = qsa('[data-mm-drawer-toggle]', shell);
      const lockKey = `drawer-${drawer.id || shell?.dataset.mmSectionId || 'mm'}`;
      const autoTabEnabled = shell?.dataset.mmMobileAutoTab !== 'false';
      let lastAutoTabAt = 0;
      let lastTouchY = 0;

      const getTabs = () => qsa('[data-mm-panel-trigger]', drawer);
      const getPanels = () => qsa('[data-mm-panel]', drawer);

      const syncDrawerToggleState = (isOpen) => {
        toggleButtons.forEach((button) => {
          button.setAttribute('aria-expanded', String(isOpen));
          button.classList.toggle('is-active', isOpen);
          qs('.mm-hamburger', button)?.classList.toggle('is-open', isOpen);
        });
      };

      const activateAdjacentPanel = (direction) => {
        const tabs = getTabs();
        const activeTab = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
        if (!activeTab) return false;

        const currentIndex = tabs.indexOf(activeTab);
        const nextIndex = currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= tabs.length) return false;

        const nextTab = tabs[nextIndex];
        const activated = activateDrawerPanel(drawer, nextTab.dataset.mmPanelTrigger);
        if (!activated) return false;

        const panels = getPanels();
        const nextPanel = panels.find((panel) => panel.dataset.mmPanel === nextTab.dataset.mmPanelTrigger);
        if (nextPanel) {
          nextPanel.scrollTop = direction > 0 ? 0 : Math.max(nextPanel.scrollHeight - nextPanel.clientHeight, 0);
        }

        return true;
      };

      const maybeAutoTab = (direction) => {
        if (!autoTabEnabled) return false;

        const now = Date.now();
        if (now - lastAutoTabAt < CFG.autoTabCooldown) {
          return false;
        }

        const switched = activateAdjacentPanel(direction);
        if (switched) {
          lastAutoTabAt = now;
        }

        return switched;
      };

      const bindPanelAutoTab = (panel) => {
        if (!panel || panel.dataset.mmAutoTabBound === 'true') return;

        panel.dataset.mmAutoTabBound = 'true';

        panel.addEventListener('wheel', (event) => {
          if (!autoTabEnabled) return;

          const atTop = panel.scrollTop <= 2;
          const atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2;

          if (event.deltaY > 0 && atBottom) {
            maybeAutoTab(1);
          } else if (event.deltaY < 0 && atTop) {
            maybeAutoTab(-1);
          }
        }, { passive: true });

        panel.addEventListener('touchstart', (event) => {
          const touch = event.touches?.[0];
          if (!touch) return;
          lastTouchY = touch.clientY;
        }, { passive: true });

        panel.addEventListener('touchmove', (event) => {
          if (!autoTabEnabled) return;

          const touch = event.touches?.[0];
          if (!touch) return;

          const currentY = touch.clientY;
          const deltaY = currentY - lastTouchY;
          lastTouchY = currentY;

          const atTop = panel.scrollTop <= 2;
          const atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2;

          if (deltaY < -6 && atBottom) {
            maybeAutoTab(1);
          } else if (deltaY > 6 && atTop) {
            maybeAutoTab(-1);
          }
        }, { passive: true });

        panel.addEventListener('touchend', () => {
          lastTouchY = 0;
        }, { passive: true });
      };

      const openDrawer = (targetGroup = '') => {
        closeCustomerPanels(shell || document);
        closeWishlistSurface();

        qsa('[data-mm-rail-layer].is-open', shell || document).forEach((layer) => {
          layer.mmClose?.();
        });

        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        syncDrawerToggleState(true);
        setBodyLock(lockKey, true);

        const panelActivated = activateDrawerPanel(drawer, targetGroup);

        if (!panelActivated && targetGroup) {
          window.setTimeout(() => {
            scrollDrawerToGroup(drawer, targetGroup);
          }, 120);
        }

        if (!targetGroup) {
          const activeTab = qs('[data-mm-panel-trigger].is-active', drawer) || qs('[data-mm-panel-trigger]', drawer);
          syncOuterRail(shell, {
            handle: activeTab?.dataset.mmPanelHandle || '',
            url: activeTab?.dataset.mmPanelUrl || '',
          });
        }
      };

      const closeDrawer = () => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        syncDrawerToggleState(false);
        setBodyLock(lockKey, false);
      };

      drawer.mmOpen = openDrawer;
      drawer.mmClose = closeDrawer;

      qsa('[data-mm-panel-trigger]', drawer).forEach((tab, index, tabs) => {
        tab.setAttribute('tabindex', index === 0 ? '0' : '-1');

        tab.addEventListener('click', () => {
          activateDrawerPanel(drawer, tab.dataset.mmPanelTrigger);
        });

        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

          event.preventDefault();
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          const nextIndex = (index + direction + tabs.length) % tabs.length;
          const nextTab = tabs[nextIndex];

          activateDrawerPanel(drawer, nextTab.dataset.mmPanelTrigger);
          nextTab.focus();
        });
      });

      getPanels().forEach(bindPanelAutoTab);
      activateDrawerPanel(drawer);

      toggleButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const targetGroup = button.dataset.mmDrawerOpenTarget || '';

          if (drawer.classList.contains('is-open') && !targetGroup) {
            closeDrawer();
            return;
          }

          openDrawer(targetGroup);
        });
      });

      backdrop?.addEventListener('click', closeDrawer);
      closeButton?.addEventListener('click', closeDrawer);

      qsa('[data-mm-search-open]', drawer).forEach((button) => {
        button.addEventListener('click', () => {
          window.setTimeout(closeDrawer, 0);
        });
      });

      qsa('[data-mm-cart-button]', drawer).forEach((button) => {
        button.addEventListener('click', () => {
          window.setTimeout(closeDrawer, 0);
        });
      });

      qsa('a[href]', drawer).forEach((link) => {
        link.addEventListener('click', () => {
          if ((link.getAttribute('href') || '') !== '#') {
            closeDrawer();
          }
        });
      });
    });
  }

  function setupCustomerPanels(root = document) {
    qsa('[data-mm-customer-panel]', root).forEach((panel) => {
      if (panel.dataset.mmCustomerPanelBound === 'true') return;

      panel.dataset.mmCustomerPanelBound = 'true';

      const shell = panel.closest('[data-mm-shell]');
      const backdrop = qs('.mm-customer-panel__backdrop', panel);
      const closeButtons = qsa('[data-mm-customer-panel-close]', panel);
      const openButtons = qsa('[data-mm-customer-panel-open]', shell || document);
      const authRoot = qs('[data-mm-account-auth]', panel);
      const viewTriggers = qsa('[data-mm-account-view-target]', panel);
      const logoutButtons = qsa('[data-mm-customer-logout]', panel);
      const lockKey = `customer-panel-${panel.id || shell?.dataset.mmSectionId || 'mm'}`;

      const setAccountView = (viewName = 'login') => {
        if (!authRoot) return;

        qsa('[data-mm-account-view]', authRoot).forEach((view) => {
          const shouldShow = view.dataset.mmAccountView === viewName;
          view.hidden = !shouldShow;
          view.classList.toggle('is-active', shouldShow);
        });

        viewTriggers.forEach((button) => {
          button.classList.toggle('is-active', button.dataset.mmAccountViewTarget === viewName);
          button.setAttribute('aria-pressed', String(button.dataset.mmAccountViewTarget === viewName));
        });

        panel.dataset.mmAccountView = viewName;
      };

      const setFormMessage = (host, type, message) => {
        if (!host) return;

        host.innerHTML = '';
        if (!message) return;

        const box = document.createElement('div');
        box.className = `mm-customer-panel__message mm-customer-panel__message--${type}`;
        box.textContent = message;
        host.appendChild(box);
      };

      const extractResponseMessage = (html, fallback) => {
        if (!html) return fallback;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const candidates = [
          '.errors',
          '.form__message',
          '.customer .errors',
          '.customer .form__message',
        ];

        for (const selector of candidates) {
          const node = doc.querySelector(selector);
          if (node && node.textContent) {
            const text = node.textContent.replace(/\s+/g, ' ').trim();
            if (text) return text;
          }
        }

        return fallback;
      };

      const responseHasErrors = (html) => {
        if (!html) return false;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Boolean(doc.querySelector('.errors, [aria-invalid="true"]'));
      };

      const syncOpenState = (isOpen) => {
        openButtons.forEach((button) => {
          button.setAttribute('aria-expanded', String(isOpen));
          button.classList.toggle('is-active', isOpen);
        });
      };

      const openPanel = (viewName = authRoot ? 'login' : null) => {
        qsa('[data-mm-search-overlay].is-open', shell || document).forEach((overlay) => {
          overlay.mmClose?.();
        });

        qsa('[data-mm-mobile-inline-search].is-active', shell || document).forEach((search) => {
          search.mmClose?.();
        });

        qsa('.mm-mobile-drawer.is-open', shell || document).forEach((drawer) => {
          drawer.mmClose?.();
        });

        qsa('.mm-rail-layer.is-open', shell || document).forEach((layer) => {
          layer.mmClose?.();
        });

        closeWishlistSurface();
        closeCustomerPanels(shell || document);

        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');

        window.requestAnimationFrame(() => {
          panel.classList.add('is-open');
          setBodyLock(lockKey, true);
          syncOpenState(true);
          if (viewName) {
            setAccountView(viewName);
          }
          qs('.mm-customer-panel__close', panel)?.focus();
        });
      };

      const closePanel = () => {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        syncOpenState(false);
        setBodyLock(lockKey, false);

        window.setTimeout(() => {
          if (!panel.classList.contains('is-open')) {
            panel.hidden = true;
          }
        }, 220);
      };

      panel.mmOpen = openPanel;
      panel.mmClose = closePanel;

      openButtons.forEach((button) => {
        if (button.dataset.mmCustomerPanelOpenBound === 'true') return;

        button.dataset.mmCustomerPanelOpenBound = 'true';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          if (panel.classList.contains('is-open')) {
            closePanel();
            return;
          }
          openPanel(authRoot ? 'login' : null);
        });
      });

      viewTriggers.forEach((button) => {
        if (button.dataset.mmAccountViewBound === 'true') return;

        button.dataset.mmAccountViewBound = 'true';
        button.addEventListener('click', () => {
          setAccountView(button.dataset.mmAccountViewTarget || 'login');
        });
      });

      qsa('form[id^="MegaMenuCustomerLogin-"]', panel).forEach((form) => {
        if (form.dataset.mmAsyncBound === 'true') return;

        form.dataset.mmAsyncBound = 'true';
        form.addEventListener('submit', async (event) => {
          event.preventDefault();

          const messageHost = qs('[data-mm-account-form-message-host]', form);
          const submitButton = qs('[data-mm-account-login-submit]', form);

          submitButton?.setAttribute('disabled', 'disabled');
          form.setAttribute('aria-busy', 'true');
          setFormMessage(messageHost, '', '');

          try {
            const response = await fetch(form.action, {
              method: 'POST',
              body: new FormData(form),
              credentials: 'same-origin',
              redirect: 'follow',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
              },
            });

            const finalUrl = new URL(response.url, window.location.origin);
            const isLoginPath = /\/account\/login\/?$/.test(finalUrl.pathname);

            if (response.ok && !isLoginPath) {
              window.location.reload();
              return;
            }

            const html = await response.text();
            const message = extractResponseMessage(html, 'Unable to sign in. Please check your email and password.');
            setAccountView('login');
            setFormMessage(messageHost, 'error', message);
            openPanel('login');
          } catch (error) {
            form.submit();
            return;
          } finally {
            submitButton?.removeAttribute('disabled');
            form.removeAttribute('aria-busy');
          }
        });
      });

      qsa('form[id^="MegaMenuRecoverForm-"]', panel).forEach((form) => {
        if (form.dataset.mmAsyncBound === 'true') return;

        form.dataset.mmAsyncBound = 'true';
        form.addEventListener('submit', async (event) => {
          event.preventDefault();

          const messageHost = qs('[data-mm-account-form-message-host]', form);
          const submitButton = qs('[data-mm-account-recover-submit]', form);

          submitButton?.setAttribute('disabled', 'disabled');
          form.setAttribute('aria-busy', 'true');
          setFormMessage(messageHost, '', '');

          try {
            const response = await fetch(form.action, {
              method: 'POST',
              body: new FormData(form),
              credentials: 'same-origin',
              redirect: 'follow',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
              },
            });

            const html = await response.text();
            const hasErrors = responseHasErrors(html);
            const message = extractResponseMessage(
              html,
              response.ok && !hasErrors
                ? 'Password reset email sent. Please check your inbox.'
                : 'We could not send the reset email. Please try again.'
            );

            setAccountView('recover');
            setFormMessage(messageHost, response.ok && !hasErrors ? 'success' : 'error', message);
            openPanel('recover');
          } catch (error) {
            form.submit();
            return;
          } finally {
            submitButton?.removeAttribute('disabled');
            form.removeAttribute('aria-busy');
          }
        });
      });

      logoutButtons.forEach((button) => {
        if (button.dataset.mmCustomerLogoutBound === 'true') return;

        button.dataset.mmCustomerLogoutBound = 'true';
        button.addEventListener('click', async () => {
          const logoutUrl = button.dataset.mmCustomerLogoutUrl;
          if (!logoutUrl) return;

          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-busy', 'true');

          try {
            await fetch(logoutUrl, {
              method: 'GET',
              credentials: 'same-origin',
              redirect: 'follow',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
              },
            });

            window.location.reload();
          } catch (error) {
            window.location.assign(logoutUrl);
          }
        });
      });

      backdrop?.addEventListener('click', closePanel);
      closeButtons.forEach((button) => {
        button.addEventListener('click', closePanel);
      });

      qsa('a[href]', panel).forEach((link) => {
        link.addEventListener('click', () => {
          if ((link.getAttribute('href') || '') !== '#') {
            closePanel();
          }
        });
      });

      const autostart = qs('[data-mm-customer-panel-autostart]', panel);
      if (autostart) {
        setAccountView(autostart.dataset.mmCustomerPanelAutostart || 'login');
        window.requestAnimationFrame(() => {
          openPanel(autostart.dataset.mmCustomerPanelAutostart || 'login');
        });
      }
    });
  }

  function setupReducedMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.style.setProperty('--mm-duration-fast', '0ms');
      document.documentElement.style.setProperty('--mm-duration', '0ms');
      document.documentElement.style.setProperty('--mm-duration-slow', '0ms');
    }
  }

  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-mm-nav-item]')) {
        closeAllMegas();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      closeAllMegas();

      qsa('[data-mm-search-overlay]').forEach((overlay) => {
        overlay.mmClose?.();
      });

      qsa('.mm-mobile-drawer.is-open').forEach((drawer) => {
        drawer.mmClose?.();
      });

      qsa('[data-mm-customer-panel].is-open').forEach((panel) => {
        panel.mmClose?.();
      });

      qsa('.mm-rail-layer.is-open').forEach((layer) => {
        layer.mmClose?.();
      });

      qsa('[data-mm-mobile-inline-search].is-active').forEach((search) => {
        search.mmClose?.();
      });

    });

    const handleViewportChange = () => {
      if (!isDesktopViewport()) {
        closeAllMegas();
      }

      qsa('.mm-mobile-drawer.is-open').forEach((drawer) => {
        if (isDesktopViewport()) {
          drawer.mmClose?.();
        }
      });

      qsa('[data-mm-customer-panel].is-open').forEach((panel) => {
        if (isDesktopViewport()) {
          panel.mmClose?.();
        }
      });

      qsa('.mm-rail-layer.is-open').forEach((layer) => {
        if (isDesktopViewport()) {
          layer.mmClose?.();
        }
      });

      queueLayoutSync();
    };

    window.addEventListener('scroll', queueLayoutSync, { passive: true });
    window.addEventListener('resize', handleViewportChange, { passive: true });

    document.addEventListener('shopify:section:load', (event) => {
      init(event.target);
    });

    document.addEventListener('shopify:section:reorder', queueLayoutSync);
  }

  function init(root = document) {
    setupDesktopMega(root);
    setupSearch(root);
    setupMobileInlineSearch(root);
    setupCartCount(root);
    setupMobileRailLayers(root);
    setupMobileRail(root);
    setupMobileDrawer(root);
    setupCustomerPanels(root);
    setupReducedMotion();
    bindGlobals();
    queueLayoutSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  window.MegaMenu = {
    refresh: () => {
      setupCartCount(document);
      queueLayoutSync();
    },
  };
})();
